import { stripEmptySectionHeadingPresentationHtml } from '@/components/tiptap/shared/section-markers';
import { writeHtmlAndMarkdownToClipboard } from '@/helpers/copyHelpers/copyHelpers-v2';
import { describeClipboardError } from '@/helpers/copyHelpers/describeClipboardError';
import { resolveClipboardPlainText } from '@/helpers/copyHelpers/plainTextClipboard';
import { simpleHtmlToMarkdown } from '@/helpers/htmlToMarkdown';
import { reformatHtml } from '@/helpers/markdownToHtml';
import { stripSectionWrappers } from '@/helpers/sectionParser';
import { getSelectionWordCounts } from '@/helpers/wordCount';
import { sessionCopyClicked } from '@/lib/analytics/scribe/session';
import {
  CopyMethod,
  SessionCopiedFormat,
  type SessionCopySource,
} from '@/models/analytics/features/sessions';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- preserve the legacy helper's dual v1/v2 analytics emission while this renderer moves to a refresh-safe .ts module
import { logger } from '@/utils/logger/logger';
import { selectionClipboardFragment } from '@heidi/editor/core';
import type { EditorView } from '@tiptap/pm/view';
import { getHTMLFromFragment } from '@tiptap/react';

/**
 * Copy selected content to clipboard as HTML and Markdown.
 * Returns true if handled, false if there is nothing to copy.
 *
 * When a ClipboardEvent is provided (copy/cut DOM events), writes via
 * event.clipboardData.setData() synchronously. This is the reliable path for
 * both keyboard shortcuts and native macOS context menu "Copy" — the latter
 * does not transfer a transient user activation into the WKWebView, so the
 * async navigator.clipboard.write() fails silently in that context.
 */
export const copySelectionToClipboard = (
  view: EditorView,
  event?: ClipboardEvent,
  copySource: SessionCopySource = 'editor_clipboard'
): boolean => {
  // Not `doc.slice(from, to)`: a `CellSelection`'s `from`/`to` span its head
  // cell, so "Select table" then ⌘C copied one cell. Shared with the
  // markdown-native twin's `buildClipboardPayload`.
  const selectedFragment = selectionClipboardFragment(view.state);

  if (selectedFragment.size === 0) {
    return false;
  }

  const selectedHtml = stripEmptySectionHeadingPresentationHtml(
    stripSectionWrappers(getHTMLFromFragment(selectedFragment, view.state.schema), {
      emitEmptySectionHeadingMarker: false,
    })
  );
  const reformattedHtml = reformatHtml(selectedHtml);
  const markdown = simpleHtmlToMarkdown({ html: selectedHtml, isCopy: true });

  // Counted before the write branches: `cut` deletes the selection as soon as
  // this returns, so the async path has nothing left to measure by the time it
  // resolves. Positional like the markdown-native twin's count, so the two
  // editors stay comparable.
  const { from, to } = view.state.selection;
  const { selectedWordCount, totalWordCount } = getSelectionWordCounts(
    view.state.doc,
    from,
    to
  );
  const trackCopy = () => {
    sessionCopyClicked({
      // Shortcut copy is verbatim: ⌘C / cut / context-menu write the highlighted
      // fragment as-is. format is therefore a constant on SHORTCUT, not a user
      // choice — unlike the note-level Copy button and (when flagged on) the
      // text-menu path.
      format: SessionCopiedFormat.FORMAT_WITH_LISTS,
      copyMethod: CopyMethod.SHORTCUT,
      selectedWordCount,
      totalWordCount,
      editorType: 'block_markdown',
      copySource,
      htmlFlavorWritten: true,
    });
  };

  if (event?.clipboardData) {
    // Synchronous write via the event object — works regardless of user gesture
    // context (no transient activation required, unlike navigator.clipboard.write())
    event.clipboardData.setData('text/html', reformattedHtml);
    event.clipboardData.setData('text/plain', resolveClipboardPlainText({ markdown }));
    // `setData` returns void, so a failure here is undetectable and this counts
    // optimistically — same blind spot as the markdown-native twin, so the two
    // editors stay comparable.
    trackCopy();
  } else {
    // No ClipboardEvent (programmatic copy): the async writer is
    // fire-and-forget at this layer, so contain rejections with a PHI-safe
    // log rather than leaving an unhandled rejection as the only signal.
    // Analytics fire only once the write resolves — a rejection that still
    // counted as a copy would inflate this editor's success rate against the
    // markdown-native twin's honest count.
    void writeHtmlAndMarkdownToClipboard({ html: reformattedHtml, markdown }).then(
      trackCopy,
      (error: unknown) => {
        logger.error(
          'Failed to copy through the vMarkdown clipboard serializer',
          describeClipboardError(error)
        );
      }
    );
  }

  return true;
};
